import { ReactElement } from 'react'
import { useMatch } from 'react-router-dom'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Button, Stack, Typography } from '@mui/material'
import { useSnackbar } from '@ui/snackbar'
import { useAppStore } from '@stores'
import InstructionRow from './InstructionRow'
import { buildInstructionsClipboardText } from './utils'

/**
 * The "Instruktionen" tab: the notes the user wrote down for the AI agent
 * (added via the note button in the header bar), with a copy button to hand
 * them all off to the agent at once.
 */
const InstructionsTab = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()

  const editorMatch = useMatch('/editor/:projectId/*')
  const projectId = editorMatch?.params.projectId

  const storedInstructions = useAppStore(state =>
    projectId ? state.aiInstructionsByProject[projectId] : undefined,
  )
  const removeAiInstruction = useAppStore(state => state.removeAiInstruction)
  const clearAiInstructions = useAppStore(state => state.clearAiInstructions)

  const instructions = storedInstructions ?? []

  const handleCopy = () => {
    navigator.clipboard.writeText(buildInstructionsClipboardText(instructions))
    enqueueSnackbar('Instruktionen in Zwischenablage kopiert', { variant: 'info' })
  }

  if (!projectId) {
    return (
      <Typography variant="body2" color="text.secondary" data-cy="ai-instructions-no-project">
        Kein Projekt geöffnet — Instruktionen werden pro Projekt gesammelt.
      </Typography>
    )
  }

  return (
    <Stack spacing={1.5} data-cy="ai-access-instructions-tab">
      <Stack spacing={1}>
        {instructions.length === 0 && (
          <Typography variant="body2" color="text.secondary" data-cy="ai-instructions-empty">
            Keine Instruktionen vorhanden. Elemente in der Szene auswählen und über das Notiz-Symbol
            eine Instruktion hinzufügen.
          </Typography>
        )}
        {instructions.map(instruction => (
          <InstructionRow
            key={instruction.id}
            instruction={instruction}
            onRemove={instructionId => removeAiInstruction(projectId, instructionId)}
          />
        ))}
      </Stack>
      {instructions.length > 0 && (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            size="small"
            onClick={() => clearAiInstructions(projectId)}
            data-cy="ai-instructions-clear"
          >
            Alle entfernen
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopy}
            data-cy="ai-instructions-copy"
          >
            Kopieren
          </Button>
        </Stack>
      )}
    </Stack>
  )
}

export default InstructionsTab
